/**
 * The grant contract - the web tier's FIRST sanctioned write, and the vocabulary the SPA,
 * the Worker and the sidecar all have to spell the same way.
 *
 * WHAT A GRANT IS. One PENDING row in `web_outbox`, action `reward.grant`: an INTENT, not a
 * reward. The web asks; the mod's `WebOutboxDrainJob` claims the row on its own tick, runs it
 * through `WebIntents` - the trust boundary, which refuses anything it will not carry out -
 * applies it on the server thread and writes the audit row. Nothing the web writes takes
 * effect until this server has agreed to it.
 *
 * THE WEB DOES NOT WRITE `pending_reward`, AND THIS IS THE SECOND VERSION OF THIS FILE.
 * The first shipped a direct INSERT into the mailbox, which had two consequences worth
 * writing down because both are the reason the channel moved:
 *   * IT WAS A SECOND POLICY. `WebIntents` refuses a `command` reward outright - a browser
 *     session or a stolen sidecar credential must not be a remote console - and a direct
 *     mailbox INSERT walked straight past that refusal. Two write channels with opposite
 *     rules on the most dangerous payload kind is not a ceiling, it is a gap.
 *   * IT COULD CREATE AN UNRECOVERABLE STATE. A payload the schema accepts but the mod
 *     cannot map (`{type:'currency'}` with no amount) became a mailbox row that
 *     `RewardService.readAndClaim` fails on for that player forever, and the write role
 *     holds no DELETE to clear it. The same payload on the outbox is settled FAILED with the
 *     domain's own message, visible in `web.outbox_status`, and no game state is touched.
 *
 * WHAT A GRANT IS NOT, and the reason this file is so short. There is no update, no delete,
 * no revoke and no "edit the queued intent". Those are not missing features - the Postgres
 * role the sidecar switches to (`POSTGRES_WRITE_ROLE`) may INSERT a PENDING intent and mint
 * a profile row, may SELECT exactly `player_profile(id, player_uuid)`, and may do nothing
 * else anywhere; `web/deploy/postgres-init/web-roles.sql` is what says so and
 * `deploy/dev-stack.sh` is what proves it. A revoke endpoint would need a privilege the
 * database refuses to hand out, which is the design working rather than a gap in it.
 *
 * THE PAYLOAD IS DELIBERATELY `unknown` HERE. It is `RewardPayload`, a sealed discriminated
 * union in `:business`, whose JSON Schema `:schema-gen` emits and the browser renders forms
 * from. Mirroring it as a TypeScript union would be a second, hand-written copy of a
 * contract that changes whenever someone adds a reward kind - exactly the rot the generated
 * schema exists to prevent. The sidecar validates the value against that generated schema
 * with ajv before any SQL is prepared, so "unknown" is the honest static type for something
 * whose real check happens at runtime against a file neither end wrote.
 */
import type { ItemSpecDocument, UuidText } from './views';

/**
 * File stem of the generated schema the sidecar validates a payload against, inside
 * `ARTIFACT_DIR/schema/`. Its `title`, used as the fallback lookup key when a future
 * generator renames the file, is {@link REWARD_PAYLOAD_SCHEMA_TITLE}.
 */
export const REWARD_PAYLOAD_SCHEMA_ID = 'reward-payload';

/** `title` of the same schema - the name the mod's own POJO carries. */
export const REWARD_PAYLOAD_SCHEMA_TITLE = 'RewardPayload';

/**
 * The `web_outbox.action` a grant is written as. `WebIntents.ACTION_REWARD_GRANT` is the
 * other end of this string, and it refuses any action it does not recognise BY NAME - so a
 * sidecar deployed ahead of the mod fails visibly instead of filing intents nobody applies.
 */
export const GRANT_ACTION = 'reward.grant';

/**
 * `web_outbox.status` the web is allowed to write, and the only one it ever writes. Mirrors
 * `OutboxStatus.PENDING.wire()`. The database enforces it too: the RLS policy's WITH CHECK
 * pins this value, so a compromised sidecar cannot file an intent that claims to have
 * already been applied.
 */
export const OUTBOX_STATUS_PENDING = 'pending';

/**
 * Every `web_outbox.status` value, mirroring the mod's `OutboxStatus` enum in lower case —
 * the spelling `OutboxStatus.wire()` persists and `web.outbox_status` hands back.
 *
 * IT EXISTS TO BE THE MIRRORED CONTRACT, and `shared.test.ts` is what pins it against the
 * enum's own spelling. `GrantPage` reads it through {@link isOutboxStatus} and a
 * `Record<OutboxStatus, string>`, so adding a status to the Java enum and to this array makes
 * the compiler demand a headline for it rather than letting the page fall through to "Filed".
 *
 * ORDERED AS AN INTENT MOVES, so the array reads as a lifecycle: the first two are in flight
 * and the last two are terminal. Only the first is ever written by the web (the RLS policy
 * pins it), and only the mod writes the rest — a browser cannot mark its own grant delivered.
 *
 * `claimed` is the state a crash can strand a row in: a drain took it and died before
 * settling it. It is shown as in-flight rather than hidden, because "stuck claimed" is a
 * real thing an admin needs to be able to see.
 */
export const OUTBOX_STATUSES = ['pending', 'claimed', 'done', 'failed'] as const;

export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];

export function isOutboxStatus(value: unknown): value is OutboxStatus {
  return typeof value === 'string' && (OUTBOX_STATUSES as readonly string[]).includes(value);
}

/**
 * True when the mod has finished with this intent, whatever it decided.
 *
 * The issuing page stops polling on exactly this predicate, so an unrecognised status — a
 * row written by a newer mod than this build of the SPA knows about — is treated as still
 * in flight. That errs towards a page that keeps refreshing rather than one that declares
 * an outcome it cannot read.
 */
export function isTerminalOutboxStatus(value: unknown): boolean {
  return value === 'done' || value === 'failed';
}

/**
 * `web_outbox.actor` — who asked, as the Worker's token knew them.
 *
 * SHARED BECAUSE BOTH ENDS HAVE TO SPELL IT IDENTICALLY. The sidecar writes it onto the row
 * from the verified claims; the SPA has to recognise the row it just caused among the other
 * intents filed for the same player, and it does that by composing the same string from the
 * same claims. Two implementations of one format would mean an admin's grant silently
 * belonging to nobody the moment either side changed its punctuation.
 *
 * THE SNOWFLAKE COMES FIRST because it is the stable half. `name` is the Discord username at
 * mint time and is display-only; a rename must not make last month's audit rows
 * unattributable, and two admins may share a display name.
 *
 * SANITISED AND BOUNDED, because this string is copied verbatim onto an audit row that
 * outlives the request, is rendered in the grant UI, and lands in the sidecar's log lines.
 * Control characters would let an actor forge a second log line; the column is
 * `varchar(128)` and an over-long value would be a database error at the very end of a
 * request that had already done its work.
 */
export function outboxActor(subject: string, name: string): string {
  // eslint-disable-next-line no-control-regex -- stripping exactly these is the point
  const clean = (value: string): string => value.replace(/[\u0000-\u001f\u007f]/g, ' ').trim();
  const cleanSubject = clean(subject) || 'unknown';
  const cleanName = clean(name);
  const actor = cleanName === '' ? `discord:${cleanSubject}` : `discord:${cleanSubject} (${cleanName})`;
  return actor.length <= OUTBOX_ACTOR_MAX_LENGTH ? actor : actor.slice(0, OUTBOX_ACTOR_MAX_LENGTH);
}

/**
 * `web_outbox.server_id` when nothing else is configured - the same literal
 * `EditorHttpClient.DEFAULT_SERVER_ID` uses and the same one the `web.*` views stamp. It
 * matters that these agree: the drain claims strictly by `server_id`, so an intent filed
 * under a name no server answers to simply sits PENDING forever.
 */
export const DEFAULT_SERVER_ID = 'default';

/** `web_outbox.server_id` is `varchar(64)`. */
export const SERVER_ID_MAX_LENGTH = 64;

/** `web_outbox.actor` is `varchar(128)`. Who asked, as the Worker's JWT knew them. */
export const OUTBOX_ACTOR_MAX_LENGTH = 128;

/**
 * The one discriminator value this channel refuses, at any depth.
 *
 * A command reward runs arbitrary server commands as the console (permission level 4). If
 * the web could ask for one, then an admin-tier Discord role - or anyone holding the
 * sidecar's `DATABASE_URL` - would be an RCON, which is precisely the authority this whole
 * outbound-only design exists to keep. `WebIntents.requireGrantableOverTheWeb` refuses it in
 * the mod, the RLS policy in `web-roles.sql` refuses it in Postgres, and the sidecar refuses
 * it before either. Three layers, because the sidecar's is the only one an attacker who owns
 * the sidecar gets to skip.
 *
 * Command rewards stay authored in config, where they are reviewed and version-controlled,
 * and are issued in-game with `/rewards give`.
 *
 * Compared the way `RewardType.parse` compares - `raw.trim().toUpperCase()` - so a payload
 * spelling `COMMAND ` maps to the same Java record and is refused as one.
 */
export const REFUSED_REWARD_TYPE = 'command';

/**
 * True when `value` is the refused discriminator, spelled however the MOD would read it.
 *
 * The trim is deliberately Java's and not JavaScript's. `String.trim()` in Java removes
 * every character at or below U+0020 - control characters included - while JS `String.trim`
 * removes Unicode whitespace and leaves U+0001 alone. Matching Java's rule means this
 * refuses exactly the set of spellings `RewardType.parse` would accept, with no gap in
 * between for someone to aim at. (The RLS predicate in `web-roles.sql` uses
 * `[[:space:][:cntrl:]]` for the same reason.)
 */
export function isRefusedRewardType(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  // eslint-disable-next-line no-control-regex -- Java's trim() is defined on exactly this range
  return value.replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, '').toLowerCase() === REFUSED_REWARD_TYPE;
}

/**
 * Largest grant request body the sidecar will read, in bytes.
 *
 * Generous next to any real payload (a nested `collection` of a hundred rewards is a few
 * kilobytes) and small next to a session bundle, because a grant is one row and the request
 * is buffered before it is parsed.
 */
export const GRANT_MAX_BYTES = 64 * 1024;

/** Longest `description` a grant may carry. It is a line in a mail GUI, not prose. */
export const GRANT_DESCRIPTION_MAX_LENGTH = 256;

/**
 * The editor tier a grant requires.
 *
 * `EDITOR_ROLES` is ordered look / change config / change grants (see auth.ts), so the
 * third tier is the one that may move value. Named here rather than written `'admin'` at
 * the call site so that the SPA's "should I draw the grant form" and the sidecar's "may I
 * accept this" are literally the same constant.
 */
export const GRANT_MIN_ROLE = 'admin';

/** Canonical hyphenated UUID, as the `:uuid` path segment must be spelled. */
export const UUID_TEXT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuidText(value: unknown): value is UuidText {
  return typeof value === 'string' && UUID_TEXT_PATTERN.test(value);
}

/** The sidecar route a grant is POSTed to. One function so nobody builds the path by hand. */
export function grantPath(playerUuid: string): string {
  return `/v1/players/${playerUuid}/grants`;
}

/**
 * A grant request body.
 *
 * The three fields are exactly `WebIntentPayload`'s three - the body of a `reward.grant`
 * intent - and nothing else. `target_id` is resolved by the sidecar from the `:uuid` path
 * segment, so a caller cannot name a profile row directly; `actor`, `action`, `server_id`
 * and `status` are the sidecar's to write and are not offered here at all.
 */
export interface GrantRequest {
  /** A `RewardPayload` document. Validated against the generated schema, never trusted. */
  readonly payload: unknown;
  /** Mail-GUI line. Null or absent means the menu renders its default. */
  readonly description?: string | null;
  /** Mailbox icon, explicitly allowed to differ from `payload`. Null means default icon. */
  readonly displayItem?: ItemSpecDocument | null;
}

/**
 * The document that ends up in `web_outbox.payload`, as `web.outbox_status` hands it back.
 *
 * IT IS NOT A {@link GrantRequest} AND IT IS NOT A BARE REWARD. The sidecar wraps the three
 * request fields into the mod's `WebIntentPayload` shape - the reward moves under a `reward`
 * key - so a page reading its own filed intent back has to unwrap it. Mirrored here rather
 * than re-derived at the call site because getting it wrong shows up as an empty summary
 * rather than as an error.
 *
 * `reward` stays `unknown` for the same reason `GrantRequest.payload` does.
 */
export interface WebIntentDocument {
  readonly reward: unknown;
  readonly description: string | null;
  readonly displayItem: ItemSpecDocument | null;
}

/** Reads an outbox row's `payload` as an intent document, or null if it is not one. */
export function asIntentDocument(payload: unknown): WebIntentDocument | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  const record = payload as Record<string, unknown>;
  if (!('reward' in record)) return null;
  const description = record['description'];
  return {
    reward: record['reward'],
    description: typeof description === 'string' ? description : null,
    displayItem: (record['displayItem'] ?? null) as ItemSpecDocument | null,
  };
}

/**
 * 201 body.
 *
 * 201 MEANS "FILED", NOT "GRANTED". The row exists and is PENDING; whether the reward is
 * ever delivered is the mod's decision, taken on its own tick, and it may refuse with a
 * reason. The UI learns the outcome from `web.outbox_status` through the read plane.
 *
 * NO ROW ID, and that is a privilege fact rather than an oversight: `insert ... returning id`
 * requires SELECT on the returned column, and the write role deliberately cannot read
 * `web_outbox` at all - not even the row it just filed. Verified against a real PostgreSQL
 * 16 (`permission denied for table web_outbox` on the RETURNING form). A UI that wants to
 * follow the intent reads it back through the view with a READ token, which is the only
 * direction data is allowed to flow.
 */
export interface GrantAccepted {
  readonly ok: true;
  readonly playerUuid: UuidText;
  /**
   * True when this grant is what created the player's `player_profile` row - a grant to
   * someone who has never joined the server. The FK normalisation makes a profile the
   * mandatory target of every owned row, so the grant path mints one, exactly as the mod's
   * `PlayerProfileRepository.findOrCreate` does. The minted row carries no name.
   */
  readonly profileMinted: boolean;
}

/** Stable machine-readable reasons a grant was refused. */
export type GrantErrorCode =
  | 'unauthorised'
  | 'forbidden'
  | 'invalid_player_uuid'
  | 'payload_too_large'
  | 'invalid_json'
  | 'invalid_request'
  | 'payload_invalid'
  /**
   * The payload is a valid `RewardPayload` and this channel still will not carry it - today
   * that is exactly {@link REFUSED_REWARD_TYPE}. Distinct from `payload_invalid` because
   * nothing is wrong with the document: editing it will not help, and the UI should say so
   * rather than pointing at a field.
   */
  | 'payload_refused'
  | 'schema_unavailable'
  | 'grants_not_configured'
  | 'database_error';

export interface GrantRejected {
  readonly error: GrantErrorCode;
  /** One sentence for a human. Never carries a connection string or an upstream 5xx body. */
  readonly detail?: string;
  /** Per-field problems, for `payload_invalid` and `invalid_request`. */
  readonly problems?: readonly string[];
}
